/**
 * Entity API Service
 * Calls raisy_BE endpoints for entity management.
 * PROJECT-031: Aligned with kernel embedding & semantic discovery patterns.
 */

import AuthUtils from "@/providers/firebase/authUtils";
import { ApiResponse } from "./kernelBotApi";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

// ── Types ──────────────────────────────────────────────────────────

export interface EntityData {
  entity_id: string;
  entitykey: string;
  entity_number: number | null;
  entity_name: string;
  entity_type: string;
  entity_type_sub: string | null;
  entity_offering_type: string | null;
  entity_offering_delivery: string | null;
  entity_status: "prospect" | "active" | "archived";
  entity_description: string | null;
  person_role: string | null;
  entity_location: string | null;
  /**
   * P093 D-081 (T0108b) — the sender's postal address, rendered in the email identity
   * footer and checked by the compliance gate wherever the recipient's jurisdiction
   * mandates one (US / CAN-SPAM). Entity-owned because the entity IS the sender (D-045):
   * asked at the first send, then prefilled and editable.
   *
   * NEVER derived from `entity_location` — that column is city-or-country and is the GEO
   * input for targeting (D-052). Putting it in a CAN-SPAM footer would pass a non-empty
   * check while shipping a non-address.
   */
  entity_postal_address: string | null;
  identity_user_id: string | null;
  parent_entity_ids: string[];
  app: string;
  industry: string | null;
  target_market: string | null;
  brand_tone: string | null;
  detected_language: string | null;
  entityDimension: number | null;
  entityDimensionSub: number | null;
  classification: string | null;
  tags: string[];
  skills: unknown[] | null;
  languages: unknown[] | null;
  about: string | null;
  founded_year: number | null;
  visibility: string;
  follower_count: number;
  connection_count: number;
  extraction_url: string | null;
  extraction_status: string;
  confidence_score: number | null;
  entity_links: Record<string, string> | null;
  created_at: string | null;
  updated_at: string | null;
  // Viewer-relative own/external classification (backlog #18) — derived per-request from
  // entity_relationships, not a stored entity property. See classify_relationship_to_viewer
  // in raisy_BE/utils/entity_utils.py. Absent/undefined on responses that don't annotate it.
  relationship_type?: string | null;
  role_title?: string | null;
  ownership?: "own" | "external" | "unknown";
  // Re-scrape conflict-review scratch data (backlog #18) — populated by /entity/merge-preview.
  pending_conflicts?: Record<string, EntityConflictField> | null;
  // PROJECT-077b T0106 — async possible-duplicate flag, set by a background check after
  // POST /user/init creates a brand-new person entity. Never auto-merged; surfaced by the
  // dashboard's "possible duplicate" banner (T0110).
  possible_duplicate_of?: PossibleDuplicateFlag | null;
  /** Per-user preferences bag — preferences[user_id] = { use_cases, revenue_stage, first_tasks, raising_stage, raising_amount, check_size, … }. */
  preferences?: Record<string, Record<string, unknown>> | null;
}

/** Mirrors ConflictReviewModal's ExistingEntityMatch shape — kept as its own type here
 * (not imported from the component) to avoid a circular import between the API layer
 * and a UI component. */
export interface PossibleDuplicateCandidate {
  entity_id: string;
  entity_name: string;
  entity_type: string;
  entity_description: string | null;
  person_role: string | null;
  entity_location: string | null;
  score: number;
}

export interface PossibleDuplicateFlag {
  candidates: PossibleDuplicateCandidate[];
  checked_at: string;
  dismissed: boolean;
}

export interface EntityConflictField {
  old: unknown;
  new: unknown;
  detected_at: string;
}

export interface EntitySearchRequest {
  name?: string;
  affiliation_entity_id?: string;
  relationship_type?: string;
  entity_type?: string;
  app?: string;
}

export interface EntitySemSearchRequest {
  name: string;
  description?: string;
  entity_type?: string;
  top_n?: number;
  threshold?: number;
  app?: string;
}

export interface EntityExactMatchRequest {
  entityDimension: number;
  entityDimensionSub: number;
}

export interface EntityBatchEmbedRequest {
  app?: string;
}

export interface EntityAutoTagRequest {
  entity_id: string;
}

export interface EntitySemSearchResult {
  entity: EntityData;
  score: number;
}

// ── Helper ─────────────────────────────────────────────────────────

const DEFAULT_TIMEOUT_MS = 20_000;

async function apiCall<T>(
  path: string,
  options: RequestInit & { timeoutMs?: number } = {},
): Promise<ApiResponse<T>> {
  const { timeoutMs = DEFAULT_TIMEOUT_MS, ...fetchOptions } = options;
  const headers = await AuthUtils.getAuthHeaders();
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(`${API_URL}${path}`, {
      ...fetchOptions,
      headers: { ...headers, ...fetchOptions.headers },
      signal: controller.signal,
    });
    return res.json();
  } finally {
    window.clearTimeout(timeoutId);
  }
}

// ── Entity Endpoints ───────────────────────────────────────────────

/** Fetch a single entity by id or entitykey. */
export async function getEntity(entity_id: string): Promise<ApiResponse<EntityData>> {
  return apiCall("/api/v1/entity/get", {
    method: "POST",
    body: JSON.stringify({ entity_id }),
  });
}

/**
 * The patch surface for `updateEntity`.
 *
 * Deliberately a closed set of fields rather than `Record<string, unknown>`: this endpoint
 * is a permission-checked write (the BE requires `editor` on the entity), so it should not
 * be possible to widen it by accident from a caller.
 */
export interface EntityUpdateRequest {
  /**
   * The page this entity can be read from — the address `/entity/merge-preview` re-scrapes when no
   * candidate data is supplied (P206 T0306, the person's own profile).
   *
   * ⚠️ THE BE HAS ALWAYS ACCEPTED THIS; THE TYPE DID NOT. `EntityUpdateRequest` is deliberately a
   * closed set (see its header), so a caller could not pass a field the endpoint was happy to take.
   * That is the same shape as the `model_tier` note further down this file, and the same lesson: a
   * narrow type is a real restriction, not a formality — it decides what the product can do.
   */
  extraction_url?: string | null;
  /**
   * P093 D-081 (T0108b) — the sender's postal address.
   *
   * OMIT the key to leave the stored address untouched; pass `null` (or a blank string) to
   * clear it. The BE applies the same `model_fields_set` idiom, and this endpoint is the
   * ONLY writer of the stored value — the send path reads it and never writes it back, so
   * automation cannot silently rewrite a compliance field.
   */
  entity_postal_address?: string | null;
}

/**
 * Patch an entity's stored fields. Requires `editor` permission on the entity.
 *
 * Only the keys present in `patch` are touched — see `EntityUpdateRequest`.
 */
export async function updateEntity(
  entity_id: string,
  patch: EntityUpdateRequest,
): Promise<ApiResponse<EntityData>> {
  return apiCall("/api/v1/entity/update", {
    method: "POST",
    body: JSON.stringify({ entity_id, ...patch }),
  });
}

/**
 * In-flight deduplication, KEYED BY REQUEST. The previous version held ONE promise regardless of
 * its arguments, so a second call arriving while the first was in flight was handed the FIRST
 * call's response. With an ownership scope in play that is not a cache miss — it is the WRONG SET
 * OF RECORDS, silently, and it is the same family as `/entities` showing every readable record
 * (2026-09-22, P013 §16.14).
 */
const listEntitiesInFlight = new Map<string, Promise<EntityListResult>>();

/**
 * What the list endpoint answers: the page of records AND the server's own count.
 *
 * `total` used to be thrown away — the helper returned the array alone — so a caller could not tell
 * "these 500 are everything" from "these 500 are the first page of 812". The server has always sent
 * it; discarding it is what made the ceiling SILENT (P013 T0416/T0428).
 */
export interface EntityListResult {
  entities: EntityData[];
  /** How many records matched the requested scope, counted by the SERVER. */
  total: number;
  /** Everything the viewer may read, whatever scope was asked for. */
  totalUnfiltered: number;
}

/**
 * List entities for the current user.
 *
 * `ownership` scopes the request SERVER-side (`own` / `external` / `unknown`, repeatable). The
 * server applies it BEFORE its limit, so the scope is exact at any size rather than a filter over
 * one page. Omit it for everything the viewer may read — the pre-existing behaviour.
 */
export async function listEntitiesWithTotal(
  limit = 500,
  ownership?: string[],
): Promise<EntityListResult> {
  const scope = ownership?.filter(Boolean).slice().sort().join(",") ?? "";
  const key = `${limit}|${scope}`;
  const inFlight = listEntitiesInFlight.get(key);
  if (inFlight) return inFlight;

  const query = new URLSearchParams({ limit: String(limit) });
  for (const value of ownership ?? []) if (value) query.append("ownership", value);

  const promise = apiCall<{ entities: EntityData[]; total: number }>(
    `/api/v1/entity/list?${query.toString()}`,
    { timeoutMs: 20_000 },
  )
    .then((res) => ({
      entities: res.data?.entities ?? [],
      total: res.data?.total ?? 0,
      totalUnfiltered: (res.data as { total_unfiltered?: number } | undefined)?.total_unfiltered ?? 0,
    }))
    .finally(() => {
      listEntitiesInFlight.delete(key);
    });

  listEntitiesInFlight.set(key, promise);
  return promise;
}

/**
 * The records alone — the shape every existing caller already expects.
 *
 * Kept as a thin wrapper rather than changed in place: nine components call it, and a signature change
 * here is not local (see the note above `EntityListResult`). Callers that need the server's count ask
 * `listEntitiesWithTotal` by name, so nothing has to guess what an array means.
 */
export async function listEntities(limit = 500, ownership?: string[]): Promise<EntityData[]> {
  return (await listEntitiesWithTotal(limit, ownership)).entities;
}

// ── The faceted slice (@P107 D-004's shape, applied to the entity graph) ─────────────────────
export interface EntityFacetValue {
  value: string;
  label: string;
  count: number;
  /** The level ABOVE this value, present only on a hierarchical facet (`ED30.40` → `ED30`). */
  parent?: string;
  parent_label?: string;
}

export interface EntityFacet {
  field: string;
  label: string;
  /** A row can hold several values for this field (e.g. tags). */
  multi: boolean;
  /** Which section of the filter panel this belongs to ("Identity", "Place", …). */
  group?: string | null;
  /**
   * True when the values form TWO LEVELS — the taxonomy case: a dimension, then the values inside
   * it. Each value then carries `parent` / `parent_label`, and the surface groups by them instead
   * of flattening two levels into one list of codes.
   */
  hierarchical?: boolean;
  /** True when the value list was cut — the UI says so instead of hiding the rest silently. */
  truncated: boolean;
  values: EntityFacetValue[];
}

export interface EntityFacetSlice {
  facets: EntityFacet[];
  /** The panel's section order, decided server-side so the surface never infers it. */
  groups: string[];
  entities: EntityData[];
  /**
   * The viewer's OWN companies that hold at least one counterparty edge — the company scope's
   * options (D-012). Counts exclude the company scope itself, so a chip's number is what clicking
   * it delivers. Only companies with a counterparty in view appear; a company is never rendered as
   * an empty zero.
   */
  companies: EntityFacetCompany[];
  /** Records matching everything EXCEPT the company scope — what *All companies* delivers. It is
   *  NOT the sum of the per-company counts: a counterparty of two companies is one record. */
  companies_total: number;
  companies_scope: string[] | null;
  /** Rows matching the filters. */
  total: number;
  /** The viewer's whole visible set, before filters — the number `total` is out of. */
  total_unfiltered: number;
  ownership: string[] | null;
  search: string;
  truncated: boolean;
}

/** One of the viewer's own companies, as a scoping option. */
export interface EntityFacetCompany {
  key: string;
  name: string;
  count: number;
}

/**
 * The company whose EDGE produced this record, with that edge's own relationship type. A record can
 * carry several — one per company — and the same record can be a `client` of one and a `partner` of
 * another, which is why the type lives here and not on the record.
 */
export interface EntityFacetBelonging {
  home_key: string;
  home_name: string;
  relationship_type: string | null;
}

export interface EntityFacetParams {
  ownership?: Array<"own" | "external" | "unknown"> | null;
  /** Scope to these OWNED companies' counterparties, by the owning side of the edge (D-012).
   *  Omit for all of them. Keys the viewer does not own are ignored server-side, not trusted. */
  companies?: string[] | null;
  filters?: Record<string, string[]> | null;
  search?: string | null;
  fields?: string[] | null;
  limit?: number;
}

/**
 * Rows AND per-value counts from ONE call, counted server-side over the viewer's whole visible
 * set — never over the rows on screen. `filters` is AND across fields, OR within a single field;
 * each field's counts are computed with every filter except its own, so a selection stays
 * widenable. NOT deduplicated: every filter change asks a different question.
 */
export async function fetchEntityFacets(params: EntityFacetParams = {}): Promise<EntityFacetSlice> {
  const res = await apiCall<EntityFacetSlice>("/api/v1/entity/facets", {
    method: "POST",
    body: JSON.stringify({
      ownership: params.ownership ?? null,
      companies: params.companies ?? null,
      filters: params.filters ?? null,
      search: params.search ?? null,
      fields: params.fields ?? null,
      limit: params.limit ?? 200,
    }),
    timeoutMs: 25_000,
  });
  return (
    res.data ?? {
      facets: [],
      groups: [],
      entities: [],
      companies: [],
      companies_total: 0,
      companies_scope: null,
      total: 0,
      total_unfiltered: 0,
      ownership: null,
      search: "",
      truncated: false,
    }
  );
}

/** SQL search by name and/or affiliation. */
export async function searchEntities(
  data: EntitySearchRequest,
): Promise<ApiResponse<{ entities: (EntityData & { similarity_score?: number })[]; total: number; search_mode: string }>> {
  return apiCall("/api/v1/entity/search", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

/** Semantic search via pgvector cosine similarity. */
export async function semSearchEntities(
  data: EntitySemSearchRequest,
): Promise<ApiResponse<{ candidates: EntitySemSearchResult[]; total: number }>> {
  return apiCall("/api/v1/entity/sem_search", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

/** Exact-match lookup for entities by ED + EDsub. */
export async function exactMatchEntities(
  data: EntityExactMatchRequest,
): Promise<ApiResponse<{ entities: EntityData[]; total: number }>> {
  return apiCall("/api/v1/entity/exact_match", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

/** Batch generate embeddings for all unembedded entities. Admin-only. */
export async function batchEmbedEntities(
  data: EntityBatchEmbedRequest = {},
): Promise<ApiResponse<{ processed: number; failed: number; skipped: number }>> {
  return apiCall("/api/v1/entity/batch-embed", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

/** Auto-generate tags for an entity via LLM. Admin-only. */
export async function autoTagEntity(
  data: EntityAutoTagRequest,
): Promise<ApiResponse<{ entity_id: string; tags: string[] }>> {
  return apiCall("/api/v1/entity/auto-tag", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

/** Soft-delete an entity. Requires owner permission. */
export async function deleteEntity(
  entity_id: string,
  cascade = false,
): Promise<ApiResponse<Record<string, never>>> {
  return apiCall("/api/v1/entity/delete", {
    method: "POST",
    body: JSON.stringify({ entity_id, cascade }),
  });
}

/**
 * Diff fresh data against an entity's stored values (PROJECT-077 T0112/T0115 — centralized
 * across every "found a possible duplicate" surface, not just re-scrape). Auto-fills
 * currently-empty fields immediately; anything that disagrees with an existing value is
 * returned in `conflicts` (also persisted server-side to pending_conflicts) for the user
 * to resolve via mergeApplyEntity.
 *
 * candidateData, when provided, is diffed against directly — no scrape happens. Omit it to
 * get the original behavior: re-scrape entity_id's own extraction_url (400 if it has none).
 * candidateSource is a free-text label (e.g. "search_resolver", "self_match") echoed back
 * to mergeApplyEntity so the eventual decision gets logged with where it came from (T0116).
 */
export async function mergePreviewEntity(
  entity_id: string,
  candidateData?: Record<string, unknown>,
  candidateSource?: string,
): Promise<ApiResponse<{ entity: EntityData; conflicts: Record<string, EntityConflictField>; auto_filled_fields: string[] }>> {
  return apiCall("/api/v1/entity/merge-preview", {
    method: "POST",
    body: JSON.stringify({
      entity_id,
      candidate_data: candidateData ?? null,
      candidate_source: candidateSource ?? null,
    }),
    timeoutMs: 45_000, // re-runs the full raisy_78 extraction pipeline when candidateData is omitted
  });
}

/** PROJECT-077b T0104 — summary of what moved when a merge-apply call retired a second,
 * duplicate entity (mergedEntityId was passed). Absent for a plain field-only refresh. */
export interface MergeSummary {
  relationships_moved: number;
  relationships_dropped_as_duplicate: number;
  permissions_moved: number;
  permissions_dropped_as_lower_privilege: number;
  integrations_moved: number;
  integrations_flagged_for_review: string[];
  scoped_records_moved: number;
}

/**
 * Apply keep_old/take_new resolutions from a prior mergePreviewEntity call.
 *
 * mergedEntityId, when provided (PROJECT-077b T0104), turns this into a full two-entity
 * merge: entity_id is the entity being kept, mergedEntityId is a second, already-existing
 * entity confirmed to be the same real-world actor — its relationships/permissions/
 * integrations move to entity_id and it's soft-retired (archived, never deleted). Omit it
 * for the original field-only refresh behavior (re-scrape / search-resolver candidate
 * data, no second entity involved).
 */
export type FieldResolution = "keep_old" | "take_new" | "custom";

export async function mergeApplyEntity(
  entity_id: string,
  resolutions: Record<string, FieldResolution>,
  candidateSource?: string,
  mergedEntityId?: string,
  customValues?: Record<string, unknown>,
): Promise<ApiResponse<EntityData & { merge_summary?: MergeSummary }>> {
  return apiCall("/api/v1/entity/merge-apply", {
    method: "POST",
    body: JSON.stringify({
      entity_id, resolutions, candidate_source: candidateSource ?? null,
      merged_entity_id: mergedEntityId ?? null,
      custom_values: customValues ?? null,
    }),
  });
}

export interface MergeFieldSuggestion {
  recommendation: "keep_old" | "take_new";
  reason: string;
  /** PROJECT-077b — a combined draft keeping what's useful from BOTH sides, for free-text
   * fields where that helps. Null for fields where merging would corrupt the value (names,
   * locations, enums) or where one side is empty. Pre-fills the popup's editable "write my
   * own" option; the user can rewrite it before saving. */
  merged_value?: string | null;
}

/**
 * AI recommendation for which side to keep, per conflicting field — deliberately its own
 * call, not folded into mergePreviewEntity's response: this hits an LLM (fast mode) and
 * must never delay the popup opening, so callers fetch it separately, in parallel, right
 * after mergePreviewEntity's conflicts come back.
 */
export async function mergeSuggestEntity(
  entity_id: string,
  conflicts: Record<string, EntityConflictField>,
): Promise<ApiResponse<{ suggestions: Record<string, MergeFieldSuggestion> }>> {
  return apiCall("/api/v1/entity/merge-suggest", {
    method: "POST",
    body: JSON.stringify({ entity_id, conflicts }),
  });
}

/** PROJECT-077b T0106/T0110 — dismiss the dashboard's "possible duplicate" banner
 * without merging anything. */
export async function dismissDuplicateFlag(entity_id: string): Promise<ApiResponse<EntityData>> {
  return apiCall("/api/v1/entity/dismiss-duplicate-flag", {
    method: "POST",
    body: JSON.stringify({ entity_id }),
  });
}

export interface EntityRelationshipData {
  entity_relationship_id: string;
  /** Full entity_id barcode, not the entitykey — serialise_entity_relationship resolves
   * both sides so the FE can compare against entity_id directly. */
  entity_id: string;
  related_entity_id: string;
  relationship_type: string;
  role_title: string | null;
}

/**
 * PROJECT-077b — is `otherEntityId` already linked to `entityId` in entity_relationships?
 *
 * Drives the wizard skip rule: the entity wizard's remaining questions exist to populate the
 * relationship row, so once that row exists there is nothing left to collect and the user
 * goes straight to the ecosystem instead of re-answering. Checks both directions because a
 * relationship is stored once, from whichever side created it — /relationship/list already
 * normalises the queried entity into `entity_id`, but comparing both is free and means a
 * change to that normalisation can't silently turn this into "always false" (which would
 * re-ask every returning user).
 *
 * Returns false on any API failure — the safe direction, since a false negative just shows
 * the wizard, while a false positive would skip collecting a role that was never recorded.
 */
export async function hasRelationshipBetween(
  entityId: string,
  otherEntityId: string,
): Promise<boolean> {
  try {
    const res = await apiCall<{ relationships: EntityRelationshipData[]; total: number }>(
      "/api/v1/entity/relationship/list",
      { method: "POST", body: JSON.stringify({ entity_id: entityId }) },
    );
    if (!res.status || !res.data) return false;
    return (res.data.relationships ?? []).some(
      (r) => r.related_entity_id === otherEntityId || r.entity_id === otherEntityId,
    );
  } catch {
    return false;
  }
}

// ── The entity-level approval default (P206 §1, "What may act without me") ──────
//
// THE HOME, READ BEFORE PROMISING IT. The section's value is the entity-level default stored in
// `entities.preferences.approval_mode`, and `raisy_BE/services/approval_mode_resolver.py` names it
// as step 4 of the resolution order (a per-process row wins over it, so this is the person's
// DEFAULT, not an override of a process he already configured). Both routes exist on the BE and had
// no front-end caller before this — `api/routes/entity_management.py:107` (GET) and `:128` (PUT),
// under the `/entity` router prefix. The write requires `editor`, which is why a section that cannot
// save must say so instead of rendering the control anyway.

/**
 * The BE's own vocabulary, taken from `set_entity_approval_mode`'s accepted set (there and nowhere
 * else) — a fifth value invented here would be refused with a 400.
 */
export const ENTITY_APPROVAL_MODES = [
  "MANUAL",
  "SEMI_AUTO",
  "FULL_AUTO",
  "CONFIDENCE_BASED",
] as const;

export type EntityApprovalMode = (typeof ENTITY_APPROVAL_MODES)[number];

/**
 * The plain-language copy for each mode — ONE home, every surface.
 *
 * `SEMI_AUTO` is the BE's word for the middle setting; showing it to a person would be showing him
 * our schema (RULES_NAMING §3, §6). The entity settings page (T0201/T0202), the Process Manager's
 * per-row badge (T0203) and the approval-settings dialog (T0205) all state the SAME meaning, so a
 * second copy of these sentences would be the same concept under two names — the defect the naming
 * rules exist to prevent. `label` is what a control shows; `meaning` is the full sentence a tooltip
 * or an inline explanation shows.
 */
export const APPROVAL_MODE_COPY: Record<EntityApprovalMode, { label: string; meaning: string }> = {
  MANUAL: {
    label: "Ask me first",
    meaning: "Nothing runs until you say so.",
  },
  SEMI_AUTO: {
    label: "Run the everyday part, ask me about the rest",
    meaning: "Routine steps go ahead; anything unusual waits for your word.",
  },
  FULL_AUTO: {
    label: "Run it, then tell me",
    meaning: "Raisy acts and reports what it did.",
  },
  CONFIDENCE_BASED: {
    label: "Run it when it is sure",
    meaning: "Confident calls go ahead; the uncertain ones wait for you.",
  },
};

/** The sentence a mode means, for a tooltip or an inline explanation. */
export function approvalModeMeaning(mode: EntityApprovalMode): string {
  return APPROVAL_MODE_COPY[mode]?.meaning ?? "";
}

/** The entity's default approval mode. Falls back to `SEMI_AUTO` on the BE when unset. */
export async function getEntityApprovalMode(entityId: string): Promise<EntityApprovalMode> {
  const r = await apiCall<{ entity_id: string; approval_mode: EntityApprovalMode }>(
    `/api/v1/entity/${encodeURIComponent(entityId)}/approval-mode`,
  );
  return r.data?.approval_mode ?? "SEMI_AUTO";
}

/**
 * Set the entity's default approval mode.
 *
 * The BE copies the preferences dict before writing it (mutating the same object is silently lost
 * because SQLAlchemy never sees the change) — a caller therefore sends the mode and nothing else,
 * and no other preference can be clobbered by this write.
 *
 * ⚠️ RETURNS THE ENVELOPE, NOT `data.approval_mode` (fixed 2026-09-25). It used to return the bare
 * mode, and its only caller judges the write with `mustSucceed(await setEntityApprovalMode(...))`,
 * which tests `status === true` on WHAT IT IS GIVEN. Handed the string `"SEMI_AUTO"`, `r.status` is
 * `undefined`, so it threw on EVERY call — successful ones included. Measured on the DEV FE against
 * `raisy_ent_03723_20260925122522`:
 *
 *   PUT HTTP 200  {"status":true,"message":"Entity approval mode set to SEMI_AUTO",
 *                  "status_code":200,"data":{"entity_id":"…","approval_mode":"SEMI_AUTO"}}
 *   the row showed  "Raisy could not save this just now — the store refused this (code unstated).
 *                    Your previous setting still stands."
 *
 * The write had landed; the row said the opposite, and told the person his previous setting still
 * stood when it no longer did. `updatePacing` in `harnessApi` returns the envelope and was the only
 * one of the three saving rows that worked — this now matches it, and `mustSucceed` unwraps it.
 */
export async function setEntityApprovalMode(
  entityId: string,
  mode: EntityApprovalMode,
): Promise<ApiResponse<{ entity_id: string; approval_mode: EntityApprovalMode }>> {
  return apiCall<{ entity_id: string; approval_mode: EntityApprovalMode }>(
    `/api/v1/entity/${encodeURIComponent(entityId)}/approval-mode`,
    { method: "PUT", body: JSON.stringify({ mode }) },
  );
}

/**
 * The three signals that may reach a person, in the PORTFOLIO'S OWN vocabulary — the keys
 * `PortfolioEntity` already serves (`process_gate`, `hermes_wake`, `preparation`). A second set of
 * names for the same three lanes would make the counts and the preference impossible to reason
 * about together (RULES_NAMING §4).
 */
export const ATTENTION_SIGNALS = ["process_gate", "hermes_wake", "preparation"] as const;

export type AttentionSignal = (typeof ATTENTION_SIGNALS)[number];

export interface EntityAttention {
  entity_id: string;
  /** An EMPTY list is a valid answer: nothing interrupts him. */
  signals: AttentionSignal[];
  /** True while nothing has ever been chosen — the counts all reach him, as they always did. */
  is_default: boolean;
  updated_by?: string | null;
  updated_at?: string | null;
}

/**
 * The caller's OWN attention preference for an entity (P206 T0302, row 5 "What needs me").
 *
 * There is deliberately no uid parameter: the route reads the caller from the token, so one person
 * cannot ask for another person's answer — the isolation is structural, not a check that could be
 * forgotten.
 */
export async function getEntityAttention(entityId: string): Promise<EntityAttention> {
  const r = await apiCall<EntityAttention>(
    `/api/v1/entity/${encodeURIComponent(entityId)}/attention`,
  );
  if (!r.data) throw new Error("the answer arrived without a value");
  return r.data;
}

/**
 * Set the caller's own attention preference. An empty array means "nothing interrupts me".
 *
 * ⚠️ RETURNS THE ENVELOPE, NOT `data` — the same defect as `setEntityApprovalMode` above, found the
 * same way on 2026-09-25. Its caller judges the write with `mustSucceed(...)`, so handing it
 * unwrapped `data` left `status` `undefined` and threw on every call — reporting a refusal for a
 * write the store had accepted. That `data` is present is NOT the verdict: `mustSucceed`'s own
 * docstring records that `data:{}` passes a `data` test but is exactly what a refusal looks like.
 */
export async function setEntityAttention(
  entityId: string,
  signals: AttentionSignal[],
): Promise<ApiResponse<EntityAttention>> {
  return apiCall<EntityAttention>(
    `/api/v1/entity/${encodeURIComponent(entityId)}/attention`,
    { method: "PUT", body: JSON.stringify({ signals }) },
  );
}
/** One row of `GET /entity/permission/list` — who else can see this company. */
export interface EntityPermissionRow {
  user_id?: string;
  permission_level?: string;
  /** The BE sends the grantee's own name/email when it has them; absent means not reported. */
  email?: string | null;
  name?: string | null;
}

/**
 * Who has access to an entity. Viewer level is enough to READ it (the route says so), so this is
 * available to a teammate as well as the owner — and an empty list is a real answer ("nobody else
 * yet"), never the same thing as a failed read.
 */
export async function listEntityPermissions(
  entityId: string,
): Promise<EntityPermissionRow[]> {
  const r = await apiCall<{ permissions?: EntityPermissionRow[]; data?: EntityPermissionRow[] }>(
    `/api/v1/entity/permission/list?entity_id=${encodeURIComponent(entityId)}`,
  );
  const payload = r.data as unknown as
    | { permissions?: EntityPermissionRow[] }
    | EntityPermissionRow[]
    | null;
  if (Array.isArray(payload)) return payload;
  return payload?.permissions ?? [];
}
